import React, { useEffect, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';

import ApiService from '../services/ApiService';
import { useAuth } from '../context/auth';
import { useHistory } from 'react-router-dom';
import MovieList from './components/MovieList';
import './components/MovieComponents.css';

const api = new ApiService();

const MachineMovies = (props) => {
    const [machineMovies, setMachineMovies] = useState([]);
    const [allMovies, setAllMovies] = useState([]);
    const [completeMoviesList, setCompleteMoviesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const { authTokens } = useAuth();
    const history = useHistory();
    const toast = useRef(null);
    
    const headers = {
        "X-Firebase-Auth-Client": authTokens.token
    };

    const movieTypeOptions = [
        { key: "1", value: "PROMOTION", text: "Promotion" },
        { key: "2", value: "ADVERTISEMENT", text: "Advertisement" }
    ];

    const handleRemoveMovie = (movie, index) => {
        try {
            const tempMovies = machineMovies.slice();
            tempMovies.splice(index, 1);
            setMachineMovies(tempMovies);
            setHasChanges(true);
            // allMovies will be updated automatically via useEffect
        } catch (error) {
            console.error('Error removing movie:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'There was an error removing the movie',
                    life: 5000
                });
            }
        }
    };

    const handleSubmit = async () => {
        setSaving(true);
        try {
            const machineId = props.location.state.machineId;

            const data = machineMovies.map(movie => ({
                id: movie.id, 
                type: movie.movieType || 'PROMOTION'
            }));
            
            await api.updateMachineMovies(machineId, data, headers);
            
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Successfully saved',
                    detail: 'Machine movies are updated',
                    life: 3000
                });
            }
            setHasChanges(false);
            
            // Refresh machine movies after successful save
            await fetchMachineMovies();
        } catch (error) {
            console.error('Error updating machine movies:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error saving',
                    detail: 'There was an error updating the machine movies',
                    life: 5000
                });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleAddMovie = (movie, index) => {
        try {
            const tempMovies = machineMovies.slice();
            tempMovies.push({
                ...movie,
                movieType: '', // geen default type
            });
            setMachineMovies(tempMovies);
            setHasChanges(true);
            // allMovies will be updated automatically via useEffect
        } catch (error) {
            console.error('Error adding movie:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'There was an error adding the movie',
                    life: 5000
                });
            }
        }
    };

    const handleMovieTypeChange = (e, { value, id }) => {
        try {
            if (machineMovies[id] !== undefined) {
                const temp = machineMovies.slice();
                temp[id].movieType = value;
                setMachineMovies(temp);
                setHasChanges(true);
            }
        } catch (error) {
            console.error('Error changing movie type:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Could not change the movie type',
                    life: 3000
                });
            }
        }
    };

    // Fetch machine movies from API
    const fetchMachineMovies = async () => {
        try {
            const machineId = props.location.state.machineId;
            const requestHeaders = {
                "X-Firebase-Auth-Client": authTokens.token
            };
            
            const machineResponse = await api.getMachine(machineId, requestHeaders);
            const machineData = machineResponse.data;
            
            // Set machine movies from API response
            setMachineMovies(machineData.movieMetadataList || []);
        } catch (error) {
            console.error('Error fetching machine movies:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Load error',
                    detail: 'Could not load machine movies. Please try again.',
                    life: 5000
                });
            }
        }
    };

    // Initial load of machine movies and all movies
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            try {
                const requestHeaders = {
                    "X-Firebase-Auth-Client": authTokens.token
                };
                const machineId = props.location.state.machineId;
                
                // Load machine movies and all movies in parallel
                const [machineResponse, allMoviesResponse] = await Promise.all([
                    api.getMachine(machineId, requestHeaders),
                    api.getAllMovies(requestHeaders)
                ]);
                
                // Set machine movies from API response
                setMachineMovies(machineResponse.data.movieMetadataList || []);
                setCompleteMoviesList(allMoviesResponse.data);
            } catch (error) {
                console.error('Error fetching initial data:', error);
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Load error',
                        detail: 'Could not load the movies. Please try again.',
                        life: 5000
                    });
                }
            } finally {
                setLoading(false);
            }
        };
        
        loadInitialData();
    }, [authTokens.token, props.location.state.machineId]);

    // Filter available movies based on machine movies (no loading state)
    useEffect(() => {
        if (completeMoviesList.length > 0) {
            const availableMovies = completeMoviesList.filter(movie =>
                !machineMovies.some(machineMovie => machineMovie.id === movie.id)
            );
            setAllMovies(availableMovies);
        }
    }, [completeMoviesList, machineMovies]);

    if (loading) {
        return (
            <div className="max-w-6xl mx-auto p-3 lg:p-4">
                <Toast ref={toast} />
                <div className="flex flex-column align-items-center justify-content-center p-6 text-600">
                    <i className="pi pi-spin pi-spinner text-4xl mb-3 text-primary"></i>
                    <p className="m-0">Loading movies...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-3 pt-0 lg:p-4 lg:pt-0">
            <Toast ref={toast} />

            {/* Action Bar - Only show when there are changes */}
            {hasChanges && (
                <div className="bg-green-50 border-1 border-green-200 border-round-lg p-3 mb-3 flex align-items-center justify-content-between shadow-1">
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-info-circle text-green-600"></i>
                        <span className="text-green-800 text-sm font-medium">You have unsaved changes</span>
                    </div>
                    
                    <Button 
                        label="Save Changes"
                        icon={saving ? "pi pi-spin pi-spinner" : "pi pi-check"}
                        onClick={handleSubmit}
                        disabled={saving}
                        severity="success"
                        size="small"
                    />
                </div>
            )}

            {saving && (
                <div className="mb-3">
                    <ProgressBar mode="indeterminate" style={{height: '6px'}} />
                </div>
            )}

            {/* Movies Grid */}
            <div className="movies-grid">
                <div className='movies-grid-item'>
                    {/* Machine Movies */}
                    <MovieList
                        movies={machineMovies}
                        title="Movies on Machine"
                        icon="pi pi-desktop"
                        isInMachine={true}
                        onRemoveMovie={handleRemoveMovie}
                        onTypeChange={handleMovieTypeChange}
                        movieTypeOptions={movieTypeOptions}
                        emptyMessage="No movies on the machine. Add movies from the available movies."
                        emptyIcon="pi pi-desktop"
                    />
                </div>
                
                <div className="movies-grid-item">
                    {/* Available Movies */}
                    <MovieList
                        movies={allMovies}
                        title="Available Movies"
                        icon="pi pi-video"
                        isInMachine={false}
                        onAddMovie={handleAddMovie}
                        emptyMessage="All movies have been added to the machine."
                        emptyIcon="pi pi-check-circle"
                    />
                </div>
            </div>
        </div>
    );
}

export default MachineMovies;
